/**
 * @file Buckets an `asOf` instant into a stable per-quarter cache identity.
 * @description Companion to `ApolloClient.tsx`'s `ComplianceOverview.keyFields`
 * (SE-8544). `complianceOverview.asOf` (server-authored, "the instant this
 * picture describes") is the only reliable signal a cache WRITE carries about
 * which period it answers — Apollo's `KeyFieldsContext` exposes `readField`
 * and the selection set, never the operation's variables, so this reads the
 * field back off the response instead of the `asOf` argument that produced it.
 *
 * ## Why bucket by quarter, not key on the raw instant
 *
 * `ComplianceOverview.keyFields`'s own doc (SE-7675) already rejected a
 * clock-keyed identity once: "`asOf` would put the CLOCK in the cache key...
 * a variable that moved every mount would fragment one normalized entry into
 * an unbounded number of them" (the SE-5058 lesson). Keying literally on
 * `asOf` would reproduce exactly that — the in-progress quarter's `asOf` is
 * clamped to "now" (`domain/compliancePeriods.ts`), so it differs on every
 * render.
 *
 * Bucketing by CALENDAR QUARTER (UTC, matching `domain/compliancePeriods.ts`'s
 * own period ids so the two never disagree about what "this quarter" means)
 * is the distinction that avoids the SE-5058 fragmentation while still giving
 * SE-8544 what it needs: two reads of the SAME quarter — whichever day within
 * it either happened to run on — share one bucket and therefore one cache
 * entity; two reads of DIFFERENT quarters (the whole point of period
 * navigation) do not.
 *
 * ## Why the quarter in progress still shares the pre-existing entity
 *
 * `ComplianceLayers`, `ClubObjectives` and `CompliancePlanStatus` are
 * deliberately merged onto ONE `ComplianceOverview` entity (SE-8547) to stop a
 * three-way refetch loop. Only `ComplianceLayers` selects `asOf` for period
 * navigation (SE-8544); the other two do not, so their writes carry no `asOf`
 * field at all and {@link periodBucketFor} always returns `undefined` for
 * them — the same "no bucket" identity as before this ticket, so they are
 * never at risk of forking onto a bucketed entity themselves.
 *
 * For `ComplianceLayers` itself, the quarter still in progress must ALSO
 * resolve to that same shared, unbucketed entity, or the ordinary "no period
 * selected yet" read would needlessly fork away from the other two readers.
 * {@link periodBucketFor} handles this by comparing the response's `asOf`
 * against `now` (at write time) and returning `undefined` whenever they land
 * in the same quarter. Only a genuinely CLOSED quarter's `asOf` resolves to
 * its own, separate bucket.
 * @module providers/compliancePeriodBucket
 */

/** Months in a calendar quarter — mirrors `domain/compliancePeriods.ts`. */
const MONTHS_PER_QUARTER = 3;

/** How many years a two-digit year label wraps at. */
const YEAR_LABEL_MODULUS = 100;

/** How many two-digit places a year label carries. */
const YEAR_LABEL_WIDTH = 2;

/**
 * The stable per-quarter id for one instant, in UTC.
 *
 * Byte-identical in format to `domain/compliancePeriods.ts`'s own period `id`
 * (`q{n}-{yy}`) — deliberately duplicated rather than imported, because
 * `providers/` sits below `features/` in this app's dependency direction
 * (`figma-design-system.md`'s hierarchy) and importing a feature module here
 * would invert it. Kept honest by
 * `__tests__/compliancePeriodBucket.crossCheck.test.ts`, which asserts the two
 * agree on every quarter boundary rather than trusting the duplication by eye.
 * @param instant - The instant to bucket.
 * @returns The quarter id, e.g. `"q3-26"`.
 */
export const quarterBucketId = (instant: Date): string => {
  const quarterIndex = Math.floor(instant.getUTCMonth() / MONTHS_PER_QUARTER);
  const year = instant.getUTCFullYear() % YEAR_LABEL_MODULUS;
  return `q${quarterIndex + 1}-${String(year).padStart(YEAR_LABEL_WIDTH, "0")}`;
};

/**
 * The `ComplianceOverview` cache-identity period bucket for one write
 * (SE-8544).
 * @param asOfIso - `complianceOverview.asOf` as the response carries it, or
 * undefined/null when the operation did not select the field at all (every
 * reader except the period-aware `ComplianceLayers` call).
 * @param now - The instant to compare against, defaulted to the real clock
 * and overridable only so tests can hold it still.
 * @returns The closed quarter's bucket id, or `undefined` for a response with
 * no `asOf` field, or one whose `asOf` falls in the quarter still in
 * progress — both cases meaning "share the ordinary, unbucketed entity".
 */
export const periodBucketFor = (
  asOfIso: string | null | undefined,
  now: Date = new Date()
): string | undefined => {
  if (asOfIso === null || asOfIso === undefined) return undefined;
  const bucket = quarterBucketId(new Date(asOfIso));
  return bucket === quarterBucketId(now) ? undefined : bucket;
};
